import { DateTime } from 'luxon';

import { getOrdinal } from '~/lib/utils';

import { GroupClassCardTagLabel } from '../calendar.util';

import { isScheduledItemRecurring } from '~/lib/util/schedule/isScheduledItemRecurring';

const ZOOM_BEFORE_AVAILABLE_MINUTES = 15;

export const GROUP_CLASS_MAX_ATTENDEES_LIMIT = 1000;

export const GROUP_SECTION_MAX_ATTENDEES_LIMIT = 1000;

export type GroupClassRegistrationCriteriaProps = {
  ageRanges: string[];
  attendeeRoles: string[];
  diagnoses: string[];
  isAllClassesRequired: boolean;
  isSensitive: boolean;
  maximumAttendees?: number;
  isRegistrationEndsAfterFirstClass: boolean;
  treatmentModalities: string[];
  treatmentPrograms: string[];
};

export const getGroupClassScheduleTitle = (
  startDate: string,
  isRecurring: boolean,
): string => {
  if (!startDate) return null;

  const formattedDate = DateTime.fromISO(startDate);

  const minuteOfDate =
    formattedDate.minute === 0
      ? formattedDate.toFormat('ha').toLowerCase()
      : formattedDate.toFormat('h:mma').toLowerCase();

  const displayRecurringFormatted = `${formattedDate.toFormat(
    'cccc',
  )}s at ${minuteOfDate}`;

  const displayNonrecurringFormatted = `${formattedDate.toFormat(
    'cccc MMMM d',
  )}${getOrdinal(formattedDate.day)} at ${minuteOfDate}`;

  return isRecurring ? displayRecurringFormatted : displayNonrecurringFormatted;
};

export const getGroupClassSchedule = (
  startDate: string,
  endDate: string,
  frequency?: RecurrenceFrequencyValue,
  isRecurring?: boolean,
): string => {
  if (!startDate) return null;

  function getFormattedDate(startDate: string) {
    return DateTime.fromISO(startDate).toFormat('LLL dd');
  }

  const isEndDateNotNull: boolean = endDate != null && endDate !== '';
  const frequencyText: string = isRecurring ? frequency : '';
  const datesText: string =
    isRecurring && isEndDateNotNull
      ? `, ${getFormattedDate(startDate)} - ${getFormattedDate(endDate)}`
      : '';

  return frequencyText + datesText;
};

export const getGroupClassExtended = (
  groupClasses: GroupClassBase[],
): GroupClassExtended[] => {
  return (groupClasses ?? []).map((groupClass) => {
    let isRecurring = false;

    if (groupClass.frequency) {
      isRecurring = isScheduledItemRecurring(groupClass.frequency);
    }

    return {
      ...groupClass,
      classification: GroupClassCardTagLabel.UNREGISTERED,
      isRecurring,
    } as GroupClassExtended;
  });
};

export const getSortByNextStartDate = (
  { nextStartDate: sourceNextStartDate }: GroupClassExtended,
  { nextStartDate: targetNextStartDate }: GroupClassExtended,
): number => {
  if (!sourceNextStartDate && !targetNextStartDate) return 0;
  if (sourceNextStartDate && !targetNextStartDate) return -1;
  if (!sourceNextStartDate && targetNextStartDate) return 1;

  const source = DateTime.fromISO(sourceNextStartDate);
  const target = DateTime.fromISO(targetNextStartDate);

  return source.toMillis() - target.toMillis();
};

export const isUserHostingGroupClass = (
  groupClass: GroupClassExtended,
  userExternalId: string,
): boolean => {
  return (groupClass?.hosts ?? []).some(
    (host) => host.externalId === userExternalId,
  );
};

export const isUserAttendingEvent = (
  attendees: GroupClassEventAttendee[] | ScheduleUserBase[],
  userExternalId: string,
): boolean => {
  return (attendees ?? []).some(
    (attendee) => attendee.externalId === userExternalId,
  );
};

export const getSortedUserGroupClasses = (
  groupClasses: GroupClassExtended[],
  userExternalId: string,
): GroupClassExtended[] => {
  if (!groupClasses || !userExternalId) return [];

  // Create a map to store all group classes in one of 3 categories
  const groupClassesMap: Record<
    | GroupClassCardTagLabel.HOSTING
    | GroupClassCardTagLabel.REGISTERED
    | GroupClassCardTagLabel.UNREGISTERED,
    GroupClassExtended[]
  > = {
    Hosting: [],
    Register: [],
    Registered: [],
  };

  // Sort all group classes
  const sortedGroupClasses = [...groupClasses.sort(getSortByNextStartDate)];

  // Add group classes to the map above in the respective category
  for (const groupClass of sortedGroupClasses) {
    if (groupClass.isUserRegistered) {
      groupClassesMap.Registered.push({
        ...groupClass,
        classification: GroupClassCardTagLabel.REGISTERED,
      });
    } else if (isUserHostingGroupClass(groupClass, userExternalId)) {
      groupClassesMap.Hosting.push({
        ...groupClass,
        classification: GroupClassCardTagLabel.HOSTING,
      });
    } else {
      groupClassesMap.Register.push({
        ...groupClass,
        classification: GroupClassCardTagLabel.UNREGISTERED,
      });
    }
  }

  return groupClassesMap.Hosting.concat(groupClassesMap.Registered).concat(
    groupClassesMap.Register,
  );
};

export const getStatusUpdatedGroupClasses = (
  groupClasses: GroupClassExtended[],
  groupClassExternalId: string,
  classification: GroupClassCardTagLabel,
): GroupClassExtended[] =>
  groupClasses.map((groupClass: GroupClassExtended) =>
    groupClass.groupClassExternalId === groupClassExternalId
      ? {
          ...groupClass,
          classification,
          isUserRegistered:
            classification === GroupClassCardTagLabel.REGISTERED,
        }
      : groupClass,
  );

export const showGroupClassEventZoomButton = ({
  end,
  isAttendee,
  isHost,
  start,
}: {
  end: string;
  isAttendee: boolean;
  isHost: boolean;
  start: string;
}): boolean => {
  return (
    DateTime.now()
      .plus({ minutes: ZOOM_BEFORE_AVAILABLE_MINUTES })
      .diff(DateTime.fromISO(start)).milliseconds > 0 &&
    DateTime.now().diff(DateTime.fromISO(end)).milliseconds < 0 &&
    (isHost || isAttendee)
  );
};

export const getRegistrationEndDate = (
  startDate: Date,
  duration: number,
): Date => {
  const registrationEndDate = new Date(startDate);
  registrationEndDate.setMinutes(startDate.getMinutes() + duration);
  return registrationEndDate;
};

export const isPastDateTime = (dateTime: string): boolean =>
  DateTime.now() > DateTime.fromISO(dateTime);

export const getGroupClassRegistrationCriteriaProps = (
  values: GroupClassRegistrationCriteriaProps,
) => ({
  ageRanges: [...values.ageRanges],
  allClassesRequired: values.isAllClassesRequired,
  attendeeRoles: [...values.attendeeRoles],
  diagnoses: [...values.diagnoses],
  markedAsSensitive: values.isSensitive,
  maximumAttendees: values.maximumAttendees,
  registrationEndsAfterFirstClass: values.isRegistrationEndsAfterFirstClass,
  treatmentModalities: [...values.treatmentModalities],
  treatmentPrograms: [...values.treatmentPrograms],
});

export enum ActivityWith {
  CARER = 'carer',
  PATIENT = 'patient',
  PROVIDER = 'provider',
  SUPPORT = 'support',
}

export const getGroupClassActivityWith = (
  activityWithReasons: NoteMultiSelectOption[],
  groupClassAttendees: GroupClassEventAttendee[],
): NoteMultiSelectOption[] => {
  const activity = new Set(
    (groupClassAttendees ?? []).reduce(
      (acc, { type }) => [
        ...acc,
        type?.toLowerCase() === ActivityWith.SUPPORT.toLowerCase()
          ? ActivityWith.CARER
          : type,
      ],
      [],
    ),
  );

  const res = activityWithReasons.filter(({ value }) =>
    activity.has(value?.toLowerCase()),
  );
  return res;
};
